import React, { useState, useEffect } from 'react';
import Button from '../ui/Button/Button';
import StatusBadge from '../ui/StatusBadge/StatusBadge';
import styles from './InvoiceModal.module.css';
import { supabase } from '../../supabaseClient';
import type { ReminderHistory, InvoiceModalProps } from '../../types';

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  invoice,
  isOpen,
  onClose,
  onSendReminder,
  onMarkAsPaid,
  onToggleAutomation
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'customer' | 'reminders' | 'integration'>('details');
  const [reminderHistory, setReminderHistory] = useState<ReminderHistory[]>([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [copiedPaymentLink, setCopiedPaymentLink] = useState(false);

  useEffect(() => {
    if (isOpen && activeTab === 'reminders') {
      fetchReminderHistory();
    }
  }, [isOpen, activeTab, invoice.id]);

  const fetchReminderHistory = async () => {
    setLoadingReminders(true);
    try {
      const { data, error } = await supabase
        .from('invoice_reminders')
        .select('*')
        .eq('invoice_id', invoice.id)
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error fetching reminder history:', error);
      } else {
        setReminderHistory(data || []);
      }
    } catch (err) {
      console.error('Error fetching reminder history:', err);
    } finally {
      setLoadingReminders(false);
    }
  };

  const getDaysOverdue = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = now.getTime() - due.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return due.getTime() < now.getTime() ? diffDays : 0;
  };

  const copyPaymentLink = async () => {
    if (invoice.payment_link) {
      try {
        await navigator.clipboard.writeText(invoice.payment_link);
        setCopiedPaymentLink(true);
        setTimeout(() => setCopiedPaymentLink(false), 2000);
      } catch (err) {
        console.error('Failed to copy payment link:', err);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Invoice {invoice.invoice_number}</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.tabContainer}>
          <div className={styles.tabButtons}>
            <button
              className={`${styles.tabButton} ${activeTab === 'details' ? styles.active : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Invoice Details
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'customer' ? styles.active : ''}`}
              onClick={() => setActiveTab('customer')}
            >
              Customer Info
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'reminders' ? styles.active : ''}`}
              onClick={() => setActiveTab('reminders')}
            >
              Reminder History
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'integration' ? styles.active : ''}`}
              onClick={() => setActiveTab('integration')}
            >
              Integration Status
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === 'details' && (
              <div className={styles.detailsTab}>
                <div className={styles.invoiceHeader}>
                  <div className={styles.invoiceAmount}>
                    <span className={styles.currency}>{invoice.currency}</span>
                    <span className={styles.amount}>{invoice.amount.toFixed(2)}</span>
                  </div>
                  <StatusBadge status={invoice.status} />
                </div>

                <div className={styles.detailsGrid}>
                  <div className={styles.detailItem}>
                    <label>Invoice Number</label>
                    <span>{invoice.invoice_number}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Customer</label>
                    <span>{invoice.customer_name}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Due Date</label>
                    <span>{formatDateOnly(invoice.due_date)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Days Overdue</label>
                    <span className={getDaysOverdue(invoice.due_date) > 0 ? styles.overdue : ''}>
                      {getDaysOverdue(invoice.due_date) > 0 ? getDaysOverdue(invoice.due_date) : 'Not overdue'}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Created</label>
                    <span>{formatDate(invoice.created_at)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Last Updated</label>
                    <span>{formatDate(invoice.updated_at)}</span>
                  </div>
                </div>

                {invoice.payment_link && (
                  <div className={styles.paymentLinkSection}>
                    <label>Payment Link</label>
                    <div className={styles.paymentLinkContainer}>
                      <input
                        type="text"
                        value={invoice.payment_link}
                        readOnly
                        className={styles.paymentLinkInput}
                      />
                      <Button
                        variant="secondary"
                        onClick={copyPaymentLink}
                        className={styles.copyButton}
                      >
                        {copiedPaymentLink ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                )}

                <div className={styles.actionButtons}>
                  <Button variant="primary" onClick={() => onSendReminder(invoice)}>
                    Send Reminder
                  </Button>
                  <Button variant="success" onClick={() => onMarkAsPaid(invoice.id)}>
                    Mark as Paid
                  </Button>
                  {invoice.payment_link && (
                    <Button
                      variant="secondary"
                      onClick={() => window.open(invoice.payment_link!, '_blank')}
                    >
                      View Payment Page
                    </Button>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'customer' && invoice.customers && (
              <div className={styles.customerTab}>
                <div className={styles.customerHeader}>
                  <h3>{invoice.customers.name}</h3>
                  {invoice.customers.is_vip && (
                    <span className={styles.vipBadge}>VIP Customer</span>
                  )}
                </div>

                <div className={styles.detailsGrid}>
                  <div className={styles.detailItem}>
                    <label>Email</label>
                    <span>{invoice.customers.email}</span>
                  </div>
                  {invoice.customers.phone && (
                    <div className={styles.detailItem}>
                      <label>Phone</label>
                      <span>{invoice.customers.phone}</span>
                    </div>
                  )}
                  {invoice.customers.address && (
                    <div className={styles.detailItem}>
                      <label>Address</label>
                      <span>{invoice.customers.address}</span>
                    </div>
                  )}
                  {invoice.customers.payment_terms && (
                    <div className={styles.detailItem}>
                      <label>Payment Terms</label>
                      <span>{invoice.customers.payment_terms}</span>
                    </div>
                  )}
                  <div className={styles.detailItem}>
                    <label>Automation Status</label>
                    <span className={invoice.customers.automation_paused ? styles.paused : styles.active}>
                      {invoice.customers.automation_paused ? 'Paused' : 'Active'}
                    </span>
                  </div>
                </div>

                {onToggleAutomation && (
                  <div className={styles.actionButtons}>
                    <Button
                      variant={invoice.customers.automation_paused ? 'warning' : 'secondary'}
                      onClick={() => onToggleAutomation(
                        invoice.customer_id,
                        invoice.customers!.automation_paused,
                        invoice.customers!.name
                      )}
                    >
                      {invoice.customers.automation_paused ? 'Resume Automation' : 'Pause Automation'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reminders' && (
              <div className={styles.remindersTab}>
                {loadingReminders ? (
                  <p>Loading reminder history...</p>
                ) : reminderHistory.length === 0 ? (
                  <p className={styles.noReminders}>No reminders sent yet for this invoice.</p>
                ) : (
                  <div className={styles.remindersList}>
                    {reminderHistory.map((reminder) => (
                      <div key={reminder.id} className={styles.reminderItem}>
                        <div className={styles.reminderHeader}>
                          <span className={styles.reminderType}>{reminder.reminder_type}</span>
                          <span className={styles.reminderDate}>{formatDate(reminder.sent_at)}</span>
                          <span className={`${styles.reminderStatus} ${styles[reminder.status]}`}>
                            {reminder.status}
                          </span>
                        </div>
                        <div className={styles.reminderSubject}>{reminder.email_subject}</div>
                        <div className={styles.reminderBody}>
                          <details>
                            <summary>View email content</summary>
                            <div dangerouslySetInnerHTML={{ __html: reminder.email_body }} />
                          </details>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'integration' && (
              <div className={styles.integrationTab}>
                <div className={styles.integrationGrid}>
                  <div className={styles.integrationItem}>
                    <label>QuickBooks Invoice ID</label>
                    <span>{invoice.quickbooks_invoice_id || 'Not synced'}</span>
                  </div>
                  <div className={styles.integrationItem}>
                    <label>Payment Link Status</label>
                    <span className={invoice.payment_link ? styles.active : styles.inactive}>
                      {invoice.payment_link ? 'Generated' : 'Not generated'}
                    </span>
                  </div>
                  {invoice.customers?.quickbooks_customer_id && (
                    <div className={styles.integrationItem}>
                      <label>QuickBooks Customer ID</label>
                      <span>{invoice.customers.quickbooks_customer_id}</span>
                    </div>
                  )}
                  {invoice.marked_paid_by_user_id && (
                    <div className={styles.integrationItem}>
                      <label>Marked Paid By</label>
                      <span>User ID: {invoice.marked_paid_by_user_id}</span>
                    </div>
                  )}
                  <div className={styles.integrationItem}>
                    <label>Last Reminder</label>
                    <span>
                      {invoice.last_reminder_sent_at 
                        ? formatDate(invoice.last_reminder_sent_at)
                        : 'Never sent'
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
